import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Card from "../Cards/Card";
import ComboHeader from "../ComboHeader/ComboHeader";
import NativeObject from "../NativeObject/NativeObject";
import { Box, Grid } from "@mui/material";
import ObjectLoader from "../ObjectLoader/ObjectLoader";
import ProgressBar from "../ProgressBar/ProgressBar";
import { useSelections } from "../../context/Selections";
import Kpi from "../KPI/Kpi";

function KpiCard({
  app,
  KPITitle,
  tooltipText,
  percentageId,
  valueId,
  valueDateId,
  goalValueId,
  goalDateId,
  hasProgressBar,
  fullView,
}) {
  const isMounted = useRef(true);
  const [percentageKPI, setPercentage] = useState(null);
  const [loadingCard, setLoadingCard] = useState(true);
  const { selections } = useSelections();

  const [value, goalValue, valueDate, goalDate] = [
    Kpi({
      app,
      objectId: valueId,
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: goalValueId,
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: valueDateId,
      updatesWithSelection: true,
    }),
    Kpi({
      app,
      objectId: goalDateId,
      updatesWithSelection: true,
    }),
  ];

  const getColorFromPercentage = (percentage) => {
    switch (true) {
      case percentage < 0.5:
        return "#E95248";
      case percentage < 0.99:
        return "#FDB949";
      default:
        return "#38BC9A";
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchKPIValue = (objectId) => {
    if (app) {
      let id = objectId;
      setLoadingCard(true);
      setTimeout(() => {
        if (!isMounted.current) return;

        return fullView
          ? app.visualization.get(id).then((res) => {
              if (!isMounted.current) return;

              const valueInfo =
                res.model.layout.qHyperCube?.qGrandTotalRow[0] ?? null;
              setPercentage({
                text: valueInfo?.qText,
                value: valueInfo?.qNum,
                color: getColorFromPercentage(valueInfo?.qNum),
              });
            })
          : setLoadingCard(false);
      }, 500);
    }
  };

  useEffect(() => {
    if (app) {
      fetchKPIValue(percentageId);
    }
  }, [selections, fullView]);

  useEffect(() => {
    if (percentageKPI) {
      setLoadingCard(false);
    }
  }, [percentageKPI]);

  return (
    <Card
      app={app}
      key={KPITitle}
      style={{
        width: "100%",
        minHeight: "140px",
        height: "17vh",
      }}
    >
      {loadingCard ? (
        <ObjectLoader
          icon="circle"
          width={"100%"}
          height={"100%"}
          className="circle-wrapper"
        />
      ) : (
        <Grid container className="kpi-wrapper">
          <Grid
            container
            flexDirection={"row"}
            justifyContent={"flex-start"}
            alignItems={"flex-start"}
          >
            <Grid
              item
              style={{
                width: "70%",
                display: "flex",
                marginBottom: "-18px",
              }}
            >
              {/* TITULO */}
              <ComboHeader
                reference={{
                  title: {
                    hard: KPITitle,
                  },
                }}
                tooltip={tooltipText ?? null}
              />
            </Grid>
          </Grid>
          <Grid
            className={`value-wrapper${hasProgressBar ? "" : "-2"}`}
            item
            xs={12}
            flexDirection={"column-reverse"}
            justifyContent={"flex-start"}
            alignItems={"flex-start"}
          >
            {hasProgressBar && (
              <Grid
                container
                flexDirection={"column-reverse"}
                justifyContent={"flex-end"}
                alignItems={"flex-start"}
                style={{
                  width: "100%",
                  height: "30%",
                  display: "flex",
                }}
              >
                {/* PROGRESS */}
                {fullView ? (
                  percentageKPI && (
                    <ProgressBar
                      key={percentageKPI}
                      bgColor={percentageKPI.color}
                      completed={percentageKPI.value * 100}
                    />
                  )
                ) : (
                  <ProgressBar key={0} bgColor={"grey"} completed={0} />
                )}
              </Grid>
            )}

            <Grid
              container
              flexDirection={"row"}
              justifyContent={"flex-start"}
              alignItems={"flex-end"}
              style={{
                marginTop: hasProgressBar ? "0" : "16px",
                width: "100%",
                height: "70%",
              }}
            >
              <Grid
                item
                className="value"
                flexDirection={"column-reverse"}
                justifyContent={"flex-end"}
                alignItems={"flex-end"}
                style={{
                  height: "100%",
                }}
              >
                {/* MAIN VALUE */}
                <Box
                  component={"span"}
                  sx={{
                    color:
                      hasProgressBar && fullView
                        ? percentageKPI?.color
                        : "#666",
                  }}
                >
                  {value}
                </Box>
              </Grid>

              {!hasProgressBar && (
                <Grid
                  item
                  className="value-date"
                  flexDirection={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"flex-end"}
                  style={{}}
                >
                  {/* VALUE DATE */}
                  <Box component={"span"}>{valueDate}</Box>
                </Grid>
              )}
            </Grid>
          </Grid>
          {fullView ? (
            <Grid
              item
              flexDirection={"row"}
              justifyContent={"flex-start"}
              alignItems="flex-end"
              style={{
                width: "100%",
                height: "30%",
                display: "flex",
              }}
            >
              {/* GOAL VALUE */}
              <Grid
                className={`goal-wrapper${hasProgressBar ? "" : "-2"} `}
                container
                flexDirection={"row"}
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
              >
                <Grid
                  item
                  flexDirection={"row"}
                  justifyContent={"flex-start"}
                  alignItems={"flex-start"}
                >
                  {/* GOAL VALUE */}
                  <Box component={"span"} className="goal-value">
                    {goalValue ?? null}
                  </Box>
                </Grid>
                {hasProgressBar ? (
                  <Grid
                    item
                    className="goal-text"
                    flexDirection={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                  >
                    <Box component={"span"}>Meta</Box>
                  </Grid>
                ) : (
                  <Grid
                    item
                    className="goal-date"
                    flexDirection={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                    style={{
                      height: "100%",
                    }}
                  >
                    {/* GOAL DATE */}
                    <Box component={"span"}>{goalDate}</Box>
                  </Grid>
                )}
              </Grid>

              {/* GOAL PERCENTAGE */}
              <Grid
                className={`percentage-wrapper${hasProgressBar ? "" : "-2"} `}
                container
                flexDirection={"row"}
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
              >
                <Grid
                  className="percentage-value"
                  item={hasProgressBar}
                  container={!hasProgressBar}
                  flexDirection={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                >
                  <Box
                    component={"span"}
                    sx={{
                      color: percentageKPI?.color,
                    }}
                  >
                    {/* PERCENTAGE */}
                    {percentageKPI?.text}
                  </Box>
                </Grid>
                {hasProgressBar && (
                  <Grid
                    className="percentage-text"
                    item
                    flexDirection={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                  >
                    <Box component={"span"}>Ating.</Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
          ) : (
            <span className="filter-warning">
              Selecione apenas 1 Mês/Ano para análise com meta
            </span>
          )}
        </Grid>
      )}
    </Card>
  );
}

KpiCard.defaultProps = {
  app: null,
  KPITitle: "",
  tooltipText: "",
  percentageId: null,
  valueId: null,
  valueDateId: null,
  goalValueId: null,
  goalDateId: null,
  hasProgressBar: false,
  fullView: true,
};

KpiCard.propTypes = {
  app: PropTypes.object,
  KPITitle: PropTypes.string,
  tooltipText: PropTypes.string,
  percentageId: PropTypes.string,
  valueId: PropTypes.string,
  valueDateId: PropTypes.string,
  goalValueId: PropTypes.string,
  goalDateId: PropTypes.string,
  hasProgressBar: PropTypes.bool,
  fullView: PropTypes.bool,
};

export default KpiCard;
